import { useContext, useEffect, useState } from "react";
import { ILoginFormValues } from "../../../authentication/_interfaces/ILoginFormValues";
import { LoginData } from "../../../../stores/models/LoginData";
import { RootContext } from "../../../../stores";
import { IAdminInvitationFlowValues } from "../_interfaces/IAdminInvitationFlowValues";

export function useLoginController(
    values: ILoginFormValues,
    allValues: IAdminInvitationFlowValues,
    update: (patch: Partial<ILoginFormValues>) => void,
    onSuccess: (override?: Partial<ILoginFormValues>) => void
) {
    const rootStore = useContext(RootContext);

    const [loginData] = useState(new LoginData());
    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ILoginFormValues, string>>>();
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    const updateLoginDataObject = (override?: Partial<ILoginFormValues>) => {
        const effectiveValues = {
            username: values.username,
            password: values.password,
            ...override
        };

        loginData.setUsername(effectiveValues.username);
        loginData.setPassword(effectiveValues.password);
    }

    useEffect(() => {
        updateLoginDataObject();
    }, [values.username, values.password]);

    useEffect(() => {
        if (validationRun) {
            setFieldErrors({
                username: loginData.errors.username,
                password: loginData.errors.password,
            });
        }
    }, [loginData.errors.username, loginData.errors.password, validationRun])

    const isValidSubmission = (override?: Partial<ILoginFormValues>) => {
        updateLoginDataObject(override);
        return loginData.validated;
    }

    const submit = async (override?: Partial<ILoginFormValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);
        loginData.setAllFieldsDirty();

        try {
            const effectiveValues = {
                ...allValues,
                ...override
            }
            const isSignUpRequest = effectiveValues.hasAccount === false;
            const shouldSwitchStepsWithoutValidation = isSignUpRequest;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                const loginResult = await rootStore.userStore.tokenBasedLogin(effectiveValues.token, effectiveValues.password || "");
                if (loginResult === undefined) {
                    setError("An unknown error occurred");
                } else if (typeof loginResult === 'string') {
                    setError("Invalid credentials");
                } else {
                    const updatedOverride = {
                        ...override,
                        userId: loginResult.id,
                        hasExistingOrganizationProfile: loginResult.hasOrganizationProfile
                    };
                    update(updatedOverride);
                    onSuccess(updatedOverride);
                }
            }
        } catch {
            setError("Invalid credentials");
        } finally {
            setSubmitting(false);
        }
    };

    return {
        values,
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}
